/**
 * Application constants – dropdown options, validation rules, and reference data.
 * Sourced from the legacy app's config/constant.ts.
 */

// ── Content Types ─────────────────────────────────────────────────────────
export const defaultContentTypes = [
  { name: 'Generic', value: 'generic' },
  { name: 'Clinical', value: 'clinical' },
  { name: 'Legal/HR', value: 'legal-hr' },
  { name: 'IT', value: 'it' },
  { name: 'Finance', value: 'finance' },
  { name: 'Commercial/Marketing', value: 'commercial-marketing' },
  { name: 'Safety/MedInfo/Ops', value: 'pharmacovigilance' },
  { name: 'Science', value: 'science' },
];

// ── Therapeutic Areas ─────────────────────────────────────────────────────
export const defaultTherapeuticAreas = [
  { value: 'Not applicable' },
  { value: 'Anesthesia' },
  { value: 'CVRM (Cardio/Renal/Metabolism)' },
  { value: 'Central Nervous System' },
  { value: 'Gastro-intestinal' },
  { value: 'Infection' },
  { value: 'Metabolism' },
  { value: 'Oncology' },
  { value: 'Pain Control' },
  { value: 'RI (Respiratory & Immunology)' },
  { value: 'Rare Diseases' },
  { value: 'Rheumatology' },
  { value: 'Vaccines & Immune Therapies' },
  { value: 'Vaccines' },
];

// ── Products ──────────────────────────────────────────────────────────────
export const defaultProducts = [
  { value: 'Not Applicable' },
  { value: 'Accolate' },
  { value: 'Alvesco' },
  { value: 'Arimidex' },
  { value: 'Atacand' },
  { value: 'Atacand Plus' },
  { value: 'AZD1222' },
  { value: 'Bevespi Aerosphere' },
  { value: 'Breztri Aerosphere (PT010)' },
  { value: 'Brilinta' },
  { value: 'Bydureon' },
  { value: 'Calquence (acalabrutinib)' },
  { value: 'Crestor' },
  { value: 'Dapagliflozin' },
  { value: 'Enhertu (trastuzumab deruxtecan)' },
  { value: 'Fasenra (benralizumab)' },
  { value: 'Faslodex' },
  { value: 'Forxiga (dapa)' },
  { value: 'Imfinzi (durvalumab) - MEDI4736' },
  { value: 'Koselugo (selumetinib)' },
  { value: 'Lokelma (AZD7270)' },
  { value: 'Lynparza (olaparib)' },
  { value: 'Saphnelo (anifrolumab)' },
  { value: 'Symbicort Turbuhaler' },
  { value: 'Tagrisso' },
  { value: 'Zoladex' },
];

// ── Desktop Publishing Requirements ──────────────────────────────────────
export interface DesktopPublishingOption {
  value: string;
  referenceFilesRequired: boolean;
}

// deprecated: this list is no longer used in the app, but is left here for reference until we determine whether to remove it entirely
export const desktopPublishingRequirements: DesktopPublishingOption[] = [
  {
    value: "My document contains images, but I don't want them translated",
    referenceFilesRequired: false,
  },
  {
    value: 'My document contains images with text and I want it translated',
    referenceFilesRequired: true,
  },
  { value: 'My document contains markup', referenceFilesRequired: true },
  {
    value:
      'This is a complex document requiring advanced typesetting after translation',
    referenceFilesRequired: true,
  },
  {
    value: 'I only require translation, no other services',
    referenceFilesRequired: false,
  },
];

// ── Target Audiences ─────────────────────────────────────────────────────
export const targetAudiences = [
  { value: 'Patients' },
  { value: 'Healthcare Professionals' },
  { value: 'AstraZeneca Internal use' },
  { value: 'General Public' },
];

// ── Document Tones ───────────────────────────────────────────────────────
export const documentTones = [{ value: 'Formal' }, { value: 'Informal' }];

// ── Valid File Types ─────────────────────────────────────────────────────
export const validFileTypes = [
  'csv',
  'dita',
  'docx',
  'html',
  'idml',
  'json',
  'markdown',
  'md',
  'pdf',
  'po',
  'pot',
  'pptx',
  'pres',
  'properties',
  'resw',
  'resx',
  'srt',
  'strings',
  'stringsdict',
  'tmx',
  'ts',
  'txt',
  'xlf',
  'xliff',
  'xlsx',
  'xml',
  'yaml',
  'yml',
];

export const validReferenceFileTypes = [...validFileTypes, 'zip'];

// ── File upload limits ───────────────────────────────────────────────────
export const MAX_FILES_PER_ZONE = 5;
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

export const requiredLangsConstants = {
  SYSTRAN: 'systran',
  XTM: 'xtm',
  SMARTLING: 'smartling',
  SYSTRANXTM: 'systran-xtm',
  ALL: 'all',
};
export const JOB_STATUSES_LEGACY = {
  DRAFT: 'DRAFT',
  CREATED: 'CREATED',
  VENDOR_CREATED: 'VENDOR_CREATED',
  AWAITING_AUTHORIZATION: 'AWAITING_AUTHORIZATION',
  AWAITING_ESTIMATION: 'AWAITING_ESTIMATION',
  AWAITING_VENDOR_SELECTION: 'AWAITING_VENDOR_SELECTION',
  AWAITING_PURCHASE_ORDER: 'AWAITING_PURCHASE_ORDER',
  PURCHASE_ORDER_SUBMITTED: 'PURCHASE_ORDER_SUBMITTED',
  IN_PROGRESS: 'IN_PROGRESS',
  CANCELLED: 'CANCELLED',
  REJECTED: 'REJECTED',
  COMPLETED: 'COMPLETED',
  DISABLED: 'DISABLED',
  DISCARDED: 'DISCARDED',
  AWAITING_VENDOR_APPROVAL: 'AWAITING_VENDOR_APPROVAL',
  AWAITING_DTP_PROCESS: 'AWAITING_DTP_PROCESS',
  APPROVED: 'APPROVED',
  ON_HOLD: 'ON_HOLD',
  DELETED: 'DELETED',
  NOT_AVAILABLE: 'NOT_AVAILABLE',
  FAILED: 'FAILED',
} as const;

export const JOB_STATUSES_ACTIVE = [
  JOB_STATUSES_LEGACY.PURCHASE_ORDER_SUBMITTED,
  JOB_STATUSES_LEGACY.APPROVED,
  JOB_STATUSES_LEGACY.IN_PROGRESS,
  JOB_STATUSES_LEGACY.AWAITING_DTP_PROCESS,
  JOB_STATUSES_LEGACY.COMPLETED,
  JOB_STATUSES_LEGACY.FAILED,
] as const;

export const JOB_STATUSES_NEW_TAGS = {
  AWAITING_ESTIMATION: 'Quotes Pending',
  AWAITING_VENDOR_APPROVAL: 'Quotes Pending',
  APPROVED: 'Quotes to be Reviewed',
  ON_HOLD: 'Quotes to be Reviewed',
  AWAITING_VENDOR_SELECTION: 'Quotes to be Reviewed',
  AWAITING_PURCHASE_ORDER: 'Quotes to be Reviewed',
  IN_PROGRESS: 'In Progress',
  AWAITING_DTP_PROCESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  REJECTED: 'Rejected',
  NOT_AVAILABLE: 'Not Supported',
} as const;

export const JOB_STATUSES_CANCELLABLE = [
  JOB_STATUSES_LEGACY.AWAITING_ESTIMATION,
  JOB_STATUSES_LEGACY.AWAITING_VENDOR_APPROVAL,
  JOB_STATUSES_LEGACY.APPROVED,
  JOB_STATUSES_LEGACY.ON_HOLD,
  JOB_STATUSES_LEGACY.AWAITING_VENDOR_SELECTION,
  JOB_STATUSES_LEGACY.AWAITING_PURCHASE_ORDER,
  JOB_STATUSES_LEGACY.IN_PROGRESS,
  JOB_STATUSES_LEGACY.AWAITING_DTP_PROCESS,
] as const;

export const isJobStatusCancellable = (
  status?: string | null,
): status is (typeof JOB_STATUSES_CANCELLABLE)[number] =>
  !!status && JOB_STATUSES_CANCELLABLE.includes(status as (typeof JOB_STATUSES_CANCELLABLE)[number]);

export const REJECTED_REQUEST_SUPPORT_EMAIL =
  'aztranslate_support@astrazeneca.com';
export const REJECTED_REQUEST_SUPPORT_SUBJECT =
  'Support needed - Rejected translation request';

export const LANGUAGES = {
  'af-ZA': 'Afrikaans (South Africa)',
  'am-ET': 'Amharic (Ethiopia)',
  am_ER: 'Amharic (Eritrea)',
  apd_SD: 'Sudanese Arabic',
  ar: 'Arabic',
  'ar-AE': 'Arabic (United Arab Emirates)',
  'ar-BH': 'Arabic (Bahrain)',
  'ar-DJ': 'Arabic (Djibouti)',
  'ar-DZ': 'Arabic (Algeria)',
  'ar-EG': 'Arabic (Egypt)',
  'ar-IQ': 'Arabic (Iraq)',
  'ar-JO': 'Arabic (Jordan)',
  'ar-KW': 'Arabic (Kuwait)',
  'ar-LB': 'Arabic (Lebanon)',
  'ar-LY': 'Arabic (Libya)',
  'ar-MA': 'Arabic (Morocco)',
  'ar-OM': 'Arabic (Oman)',
  'ar-QA': 'Arabic (Qatar)',
  'ar-SA': 'Arabic (Saudi Arabia)',
  'ar-SD': 'Arabic (Sudan)',
  'ar-SY': 'Arabic (Syria)',
  'ar-TN': 'Arabic (Tunisia)',
  'ar-YE': 'Arabic (Yemen)',
  ar_EH: 'Arabic (Western Sahara)',
  ar_MR: 'Arabic (Mauritania)',
  ar_PS: 'Arabic (Palestinian Territory)',
  ar_TD: 'Arabic (Chad)',
  'as-IN': 'Assamese',
  ay_BO: 'Aymara',
  'az-AZ': 'Azerbaijani (Azerbaijan)',
  'az-IR': 'Southern Azerbaijani',
  az_AZ_Latn: 'Azeri (Latin - Azerbaijan)',
  ba_RU: 'Bashkir',
  bam: 'Bambara',
  be_BY: 'Belarusian (Belarus)',
  'bg-BG': 'Bulgarian (Bulgaria)',
  bn: 'Bengali (International)',
  'bn-BD': 'Bengali (Bangladesh)',
  'bn-IN': 'Bengali (India)',
  bo: 'Tibetan',
  'bs-BA': 'Bosnian',
  bs_BA_Latn: 'Bosnian (Latin)',
  ca: 'Catalan',
  'ca-ES': 'Catalan (Spain)',
  ceb: 'Cebuano (used in Philippines)',
  ckb: 'Kurdish (Sorani)',
  co: 'Corsican',
  cs: 'Czech (International)',
  'cs-CZ': 'Czech',
  'cy-GB': 'Welsh',
  da: 'Danish',
  'da-DK': 'Danish',
  de: 'German (International)',
  'de-AT': 'German (Austria)',
  'de-BE': 'German (Belgium)',
  'de-CH': 'German (Switzerland)',
  'de-CN': 'German (China)',
  'de-DE': 'German (Germany)',
  'de-LI': 'German (Liechtenstein)',
  'de-LU': 'German (Luxembourg)',
  'de-NA': 'German (Namibia)',
  'de-PL': 'German (Poland)',
  'de-SK': 'German (Slovakia)',
  'de-US': 'German (United States)',
  dv_IN: 'Divehi',
  ee_GH: 'Ewe (Ghana)',
  el: 'Greek (International)',
  'el-CY': 'Greek (Cyprus)',
  'el-GR': 'Greek',
  en: 'English (Neutral)',
  en_GB: 'English (UK)',
  en_US: 'English (US)',
  eo: 'Esperanto',
  es: 'Spanish (International)',
  'es-AD': 'Spanish (Andorra)',
  'es-AR': 'Spanish (Argentina)',
  'es-BO': 'Spanish (Bolivia)',
  'es-CL': 'Spanish (Chile)',
  'es-CN': 'Spanish (China)',
  'es-CO': 'Spanish (Colombia)',
  'es-CR': 'Spanish (Costa Rica)',
  'es-DO': 'Spanish (Dominican Republic)',
  'es-EC': 'Spanish (Ecuador)',
  'es-ES': 'Spanish (Spain)',
  'es-GT': 'Spanish (Guatemala)',
  'es-HN': 'Spanish (Honduras)',
  'es-LA': 'Spanish (Latin America)',
  'es-MS': 'Spanish (Montserrat)',
  'es-MX': 'Spanish (Mexico)',
  'es-NI': 'Spanish (Nicaragua)',
  'es-PA': 'Spanish (Panama)',
  'es-PE': 'Spanish (Peru)',
  'es-PR': 'Spanish (Puerto Rico)',
  'es-PY': 'Spanish (Paraguay)',
  'es-SV': 'Spanish (El Salvador)',
  'es-US': 'Spanish (USA)',
  'es-UY': 'Spanish (Uruguay)',
  'es-VE': 'Spanish (Venezuela)',
  es_CU: 'Spanish (Cuba)',
  es_NT: 'Spanish (Neutral)',
  'et-EE': 'Estonian',
  eu: 'Basque',
  'eu-ES': 'Basque',
  'fa-AF': 'Dari/Persian (Afghanistan)',
  'fa-IR': 'Persian',
  fi: 'Finnish (International)',
  'fi-FI': 'Finnish',
  fj: 'Fijian',
  fo_FO: 'Faroese (Faroe Islands)',
  'fp-PH': 'Filipino',
  fr: 'French (International)',
  'fr-BE': 'French (Belgium)',
  'fr-BJ': 'French (Benin)',
  'fr-CA': 'French (Canada)',
  'fr-CG': 'French (Congo)',
  'fr-CH': 'French (Switzerland)',
  'fr-CI': 'French (Ivory Coast)',
  'fr-CM': 'French (Cameroon)',
  'fr-CN': 'French (China)',
  'fr-DJ': 'French (Djibouti)',
  'fr-DZ': 'French (Algeria)',
  'fr-FR': 'French (France)',
  'fr-GA': 'French (Gabon)',
  'fr-GN': 'French (Guinea)',
  'fr-GP': 'French (Guadeloupe)',
  'fr-HT': 'French (Haiti)',
  'fr-LB': 'French (Lebanon)',
  'fr-LU': 'French (Luxembourg)',
  'fr-MA': 'French (Morocco)',
  'fr-MC': 'French (Monaco)',
  'fr-MG': 'French (Madagascar)',
  'fr-MQ': 'French (Martinique)',
  'fr-MU': 'French (Mauritius)',
  'fr-MZ': 'French (Mozambique)',
  'fr-RW': 'French (Rwanda)',
  'fr-SN': 'French (Senegal)',
  'fr-TN': 'French (Tunisia)',
  'fr-US': 'French (United States)',
  fy: 'Frisian',
  'ga-IE': 'Irish (Gaelic)',
  gd_GB: 'Scottish Gaelic (Scotland)',
  'gl-ES': 'Galician (Spain)',
  grn: 'Guarani',
  'gu-IN': 'Gujarati',
  ha: 'Hausa',
  'ha-NE': 'Hausa (Niger)',
  haw: 'Hawaiian',
  he: 'Hebrew (he)',
  'he-IL': 'Hebrew',
  hi: 'Hindi',
  'hi-IN': 'Hindi',
  hm: 'Hmong',
  'hr-HR': 'Croatian',
  'ht-HT': 'Haitian Creole',
  hu: 'Hungarian',
  'hu-RO': 'Hungarian (Romania)',
  'hy-AM': 'Armenian',
  id: 'Indonesian (International)',
  'id-ID': 'Indonesian',
  ig: 'Igbo',
  ilo: 'Ilocano',
  'is-IS': 'Icelandic',
  it: 'Italian (International)',
  'it-CH': 'Italian (Switzerland)',
  'it-CN': 'Italian (China)',
  'it-IT': 'Italian',
  'it-US': 'Italian (United States)',
  iu: 'Inuktitut',
  ja: 'Japanese (International)',
  'ja-CN': 'Japanese (China)',
  'ja-JP': 'Japanese',
  'ja-US': 'Japanese (United States)',
  jv: 'Javanese',
  'ka-GE': 'Georgian',
  'kk-KZ': 'Kazakh',
  km_KH: 'Khmer',
  kmr: 'Kurdish (Kurmanji)',
  'kn-IN': 'Kannada (India)',
  ko: 'Korean (International)',
  'ko-CN': 'Korean (China)',
  'ko-KR': 'Korean',
  'ko-US': 'Korean (United States)',
  kok_IN: 'Konkani (India)',
  kri: 'Krio',
  ks: 'Kashmiri',
  'ku-CB': 'Kurdish (Sorani) LTR',
  'ku-TR': 'Kurdish (Türkiye)',
  ku_IQ: 'Kurdish (Iraq)',
  'ky-KG': 'Kirghiz; Kyrgyz (Kyrgyzstan)',
  la: 'Latin',
  lb_LU: 'Luxembourgish',
  ln_CG: 'Lingala',
  'lo-LA': 'Lao (Lao)',
  lo_LA: 'Laothian',
  'lt-LT': 'Lithuanian',
  lug: 'Luganda',
  lus: 'Mizo Chin',
  'lv-LV': 'Latvian',
  'mg-MG': 'Malagasy (Madagascar)',
  mi: 'Maori (International)',
  'mi-NZ': 'Maori',
  'mk-MK': 'Macedonian',
  'ml-IN': 'Malayalam (India)',
  'mn-MN': 'Mongolian',
  mni: 'Manipuri',
  'mr-IN': 'Marathi',
  ms: 'Malay (International)',
  'ms-BN': 'Malay (Brunei)',
  'ms-MY': 'Malay (Malaysia)',
  'mt-MT': 'Maltese',
  my: 'Burmese',
  'my-MM': 'Burmese',
  'my-SG': 'Malay (Singapore)',
  'nb-NO': 'Norwegian Bokmaal',
  'ne-NP': 'Nepali',
  nl: 'Dutch (International)',
  'nl-BE': 'Dutch (Belgium)',
  'nl-NL': 'Dutch',
  no: 'Norwegian',
  ny: 'Nyanja',
  om_ET: 'Oromo',
  'or-IN': 'Oriya',
  'pa-IN': 'Punjabi (India)',
  pa_PA: 'Punjabi',
  pl: 'Polish (International)',
  'pl-PL': 'Polish',
  prs_AF: 'Dari',
  ps: 'Pashto',
  pt: 'Portuguese (International)',
  'pt-AO': 'Portuguese (Angola)',
  'pt-BR': 'Portuguese (Brazil)',
  'pt-MZ': 'Portuguese (Mozambique)',
  'pt-PT': 'Portuguese',
  qu_PE: 'Quechua',
  ro: 'Romanian (International)',
  'ro-HU': 'Romanian (Hungary)',
  'ro-MD': 'Romanian (Moldova)',
  'ro-RO': 'Romanian',
  ru: 'Russian (International)',
  'ru-BY': 'Russian (Belarus)',
  'ru-CN': 'Russian (China)',
  'ru-EE': 'Russian (Estonia)',
  'ru-GE': 'Russian (Georgia)',
  'ru-KZ': 'Russian (Kazakistan)',
  'ru-LT': 'Russian (Lithuania)',
  'ru-MD': 'Russian (moldova)',
  'ru-RU': 'Russian',
  'ru-TJ': 'Russian (Tajikistan)',
  'ru-UA': 'Russian (Ukraine)',
  'ru-US': 'Russian (United States)',
  'rw-RW': 'Kinyarwanda (Rwanda)',
  sa_IN: 'Sanskrit',
  sd: 'Sindhi',
  'si-LK': 'Sinhala; Sinhalese (Sri Lanka)',
  si_LK: 'Singhalese',
  'sk-SK': 'Slovak',
  'sl-SI': 'Slovenian',
  sm: 'Samoan',
  sn: 'Shona',
  'so-SO': 'Somali',
  'sq-AL': 'Albanian',
  'sr-BA': 'Serbian (Bosnia and Herzegovina)',
  'sr-ME': 'Serbian (Montenegro)',
  'sr-RS': 'Serbian',
  'sr-YR': 'Serbian (Cyrillic)',
  sr_RS_Latn: 'Serbian – Serbia (Latin)',
  st: 'Sesotho',
  sv: 'Swedish (International)',
  'sv-FI': 'Swedish (Finland)',
  'sv-SE': 'Swedish',
  sw: 'Swahili',
  'sw-CD': 'Swahili (Congo - Kinshasa)',
  'sw-KE': 'Swahili (Kenya)',
  'sw-TZ': 'Swahili (Tanzania)',
  'sw-UG': 'Swahili (Uganda)',
  ta: 'Tamil (International)',
  'ta-IN': 'Tamil',
  'ta-LK': 'Tamil (Sri Lanka)',
  'te-IN': 'Telugu',
  tg_TJ: 'Tajik',
  th: 'Thai (International)',
  'th-TH': 'Thai',
  ti: 'Tigrinya',
  'ti-ET': 'Tigrinya (Ethiopia)',
  tk: 'Turkmen',
  'tk-TM': 'Turkmen',
  'tl-PH': 'Tagalog',
  tn_ZA: 'Setswana (Africa)',
  'to-TO': 'Tonga',
  tr: 'Turkish (International)',
  'tr-TR': 'Turkish',
  ts_ZA: 'Tsonga',
  'tt-RU': 'Tatar',
  tw: 'Twi',
  ty: 'Tahitian',
  ug: 'Uighur; Uyghur',
  ug_CN: 'Uyghur (China)',
  uk: 'Ukrainian',
  'uk-UA': 'Ukrainian',
  ur: 'Urdu',
  'ur-IN': 'Urdu',
  'ur-PK': 'Urdu (Pakistan)',
  'uz-UZ': 'Uzbek (Uzbekistan)',
  uz_AF: 'Uzbek (Afghanistan)',
  uz_UZ_Latn: 'Uzbek (Latin)',
  vi: 'Vietnamese',
  'vi-VN': 'Vietnamese (Vietnam)',
  'xh-ZA': 'Xhosa (South Africa)',
  yi: 'Yiddish',
  yo: 'Yoruba',
  zh: 'Chinese (zh)',
  'zh-CN': 'Chinese (Simplified)',
  'zh-HK': 'Chinese (Hong Kong)',
  'zh-MO': 'Chinese (Macau)',
  'zh-MY': 'Chinese Simplified (Malaysia)',
  'zh-SG': 'Chinese (Singapore)',
  'zh-TA': 'Chinese (Taiwan)',
  'zh-TW': 'Chinese (Traditional)',
  'zh-US': 'Chinese (United States)',
  zh_YUE: 'Chinese (Cantonese)',
  'zu-ZA': 'Zulu (South Africa)',
} as const;

export enum TranslationFileType {
  SOURCE = 1,
  REFERENCE = 2,
  TARGET = 3,
}

export const priceLimit = {
  min: 1000,
  max: 50000,
};

// ── Reclassification Qualifying Statuses ──────────────────────────────
export const RECLASSIFICATION_QUALIFYING_STATUSES = new Set([
  'AWAITING_VENDOR_SELECTION',
  'AWAITING_PURCHASE_ORDER',
  'AWAITING_DTP_PROCESS',
  'APPROVED',
  'ON_HOLD',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
  'FINAL_REVIEW',
]);

export const QUALIFYING_STATUSES_FOR_REVISED_DTP = new Set([
  'AWAITING_DTP_PROCESS',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
]);

export const TRANSLATED_FILES_ENABLED_STATUSES = [
  'IN_PROGRESS',
  'AWAITING_DTP_PROCESS',
  'COMPLETED',
] as const;
